/**
 * Module Settings Page
 *
 * A settings page that allows admins to manage module activation and features.
 * Shows toggle switches for self-hosted mode, or a "contact support" message for cloud mode.
 */

import type { BuiltinModuleKey } from '@cloud/modules'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight, Blocks, Check, Loader2, Package, Settings, X, Zap, ZapOff } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useQuery } from '@tanstack/react-query'
import { client, orpc } from '@/lib/orpcClient'
import { useModulesContext } from '@/providers/modules-provider'

export const Route = createFileRoute('/settings/modules/')({
  component: ModuleSettingsPage,
})

// Module display names and icons
const moduleInfo: Record<
  BuiltinModuleKey,
  { name: string; description: string; icon: React.ComponentType<{ className?: string }> }
> = {
  hr: {
    name: 'Human Resources',
    description: 'Manage employees, attendance, leave, and payroll',
    icon: Package,
  },
  finance: {
    name: 'Finance',
    description: 'Invoices, expenses, accounts, and financial reports',
    icon: Package,
  },
  inventory: {
    name: 'Inventory',
    description: 'Products, stock, warehouse, and suppliers',
    icon: Package,
  },
  crm: {
    name: 'CRM',
    description: 'Contacts, companies, deals, and pipeline management',
    icon: Package,
  },
  projects: {
    name: 'Projects',
    description: 'Projects, tasks, time tracking, and milestones',
    icon: Package,
  },
}

function ModuleSettingsPage() {
  const { modules, isLoading, refetch } = useModulesContext()
  const [activatingModule, setActivatingModule] = useState<string | null>(null)
  const [deactivatingModule, setDeactivatingModule] = useState<string | null>(null)
  const [togglingFeature, setTogglingFeature] = useState<{
    moduleKey: string
    featureKey: string
  } | null>(null)

  // Get cloud/self-hosted mode from backend
  const { data: modeData, isLoading: modeLoading } = useQuery(orpc.config.getMode.queryOptions())
  const isSelfHosted = !modeData?.isCloudMode

  const handleActivateModule = async (moduleKey: BuiltinModuleKey) => {
    setActivatingModule(moduleKey)
    try {
      await client.modules.activate({ moduleKey })
      toast.success(`${moduleInfo[moduleKey].name} module activated`)
      refetch()
    } catch {
      toast.error(`Failed to activate ${moduleInfo[moduleKey].name} module`)
    } finally {
      setActivatingModule(null)
    }
  }

  const handleDeactivateModule = async (moduleKey: BuiltinModuleKey) => {
    setDeactivatingModule(moduleKey)
    try {
      await client.modules.deactivate({ moduleKey })
      toast.success(`${moduleInfo[moduleKey].name} module deactivated`)
      refetch()
    } catch {
      toast.error(`Failed to deactivate ${moduleInfo[moduleKey].name} module`)
    } finally {
      setDeactivatingModule(null)
    }
  }

  const handleToggleFeature = async (
    moduleKey: BuiltinModuleKey,
    featureKey: string,
    enabled: boolean,
  ) => {
    setTogglingFeature({ moduleKey, featureKey })
    try {
      if (enabled) {
        await client.modules.features.enable({ moduleKey, featureKey })
        toast.success('Feature enabled')
      } else {
        await client.modules.features.disable({ moduleKey, featureKey })
        toast.success('Feature disabled')
      }
      refetch()
    } catch {
      toast.error('Failed to update feature')
    } finally {
      setTogglingFeature(null)
    }
  }

  // Get status badge variant
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="bg-green-500">
            Active
          </Badge>
        )
      case 'paused':
        return <Badge variant="secondary">Paused</Badge>
      case 'disabled':
        return <Badge variant="outline">Disabled</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  if (isLoading || modeLoading) {
    return <ModuleSettingsSkeleton />
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Settings className="w-5 h-5" />
          <h1 className="text-2xl font-bold">Module Settings</h1>
        </div>
        <p className="text-muted-foreground">
          Manage which modules are available for your organization.
        </p>
      </div>

      {/* Custom modules shortcut — routes to the builder */}
      <Link to="/settings/custom-modules" className="block mb-6">
        <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
          <CardContent className="py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-primary/10 p-2">
                <Blocks className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Custom modules</p>
                <p className="text-sm text-muted-foreground">
                  Build your own entities, forms, and workflows without writing code.
                </p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-muted-foreground" />
          </CardContent>
        </Card>
      </Link>

      {/* Cloud mode notice */}
      {!isSelfHosted && (
        <Card className="mb-6 border-yellow-200 bg-yellow-50 dark:bg-yellow-950">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="text-yellow-600 dark:text-yellow-400 mt-0.5">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
                  Managed by your plan
                </h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  In cloud mode, modules are managed centrally. Contact support to change your
                  active modules.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modules List */}
      <div className="space-y-4">
        {modules.map((module) => {
          const info = moduleInfo[module.key as BuiltinModuleKey]
          const Icon = info?.icon || Package
          const isActivating = activatingModule === module.key
          const isDeactivating = deactivatingModule === module.key

          return (
            <Card key={module.key}>
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{info?.name || module.name}</CardTitle>
                      <CardDescription>{info?.description || module.description}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(module.status)}
                    {isSelfHosted && module.status !== 'active' && (
                      <Button
                        size="sm"
                        onClick={() => handleActivateModule(module.key as BuiltinModuleKey)}
                        disabled={isActivating}
                      >
                        {isActivating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Zap className="w-4 h-4 mr-1" />
                            Activate
                          </>
                        )}
                      </Button>
                    )}
                    {isSelfHosted && module.status === 'active' && !module.isCore && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeactivateModule(module.key as BuiltinModuleKey)}
                        disabled={isDeactivating}
                      >
                        {isDeactivating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <ZapOff className="w-4 h-4 mr-1" />
                            Deactivate
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              {/* Features - only show if module is active */}
              {module.status === 'active' && module.features.length > 0 && (
                <>
                  <Separator />
                  <CardContent className="pt-4">
                    <h4 className="text-sm font-medium mb-3">Features</h4>
                    <div className="space-y-3">
                      {module.features.map((feature) => {
                        const isToggling =
                          togglingFeature?.moduleKey === module.key &&
                          togglingFeature?.featureKey === feature.featureKey

                        return (
                          <div
                            key={feature.featureKey}
                            className="flex items-center justify-between"
                          >
                            <div>
                              <p className="text-sm font-medium">{feature.featureKey}</p>
                              <p className="text-xs text-muted-foreground">Feature configuration</p>
                            </div>
                            {isSelfHosted ? (
                              <Switch
                                checked={feature.enabled}
                                onCheckedChange={(checked) =>
                                  handleToggleFeature(
                                    module.key as BuiltinModuleKey,
                                    feature.featureKey,
                                    checked,
                                  )
                                }
                                disabled={isToggling}
                              />
                            ) : feature.enabled ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <X className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </CardContent>
                </>
              )}
            </Card>
          )
        })}
      </div>
    </div>
  )
}

function ModuleSettingsSkeleton() {
  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-96" />
      </div>

      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div>
                  <Skeleton className="h-5 w-32 mb-1" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  )
}
